import { useState, useEffect } from 'react'

import { Modal, notification, Button, Tabs } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'

import ReasonsRadioGroup from './ReasonsRadioGroup'
import { createRejectionReason, deleteRejectionReason, updateRejectionReason } from '../../../utils/api/rejectionReason'
import { selectUser } from '../../../reducers/UserReducer'
import { refuseMaccsInternship } from '../../../utils/api/maccsInternship'
import { generalErrorHandler } from '../../../utils'
import SmartTable, { SmartTableDataTypes } from '../../shared/SmartTable'
import { getInstitutionRejectionReasons } from '../../../utils/api/institution'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

const TabPane = Tabs.TabPane

const InternshipRejectionModal = ({ internship, onClose }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedReason, setSelectedReason] = useState(null)
  const [rejectionReasons, setRejectionReasons] = useState([])
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (internship) {
      getInstitutionRejectionReasons(user, internship.institution).then(({ data }) => setRejectionReasons(data))
    }
  }, [internship])

  const columns = [
    { type: SmartTableDataTypes.ID, key: 'id' },
    { type: SmartTableDataTypes.STRING, name: t('Reason'), key: 'name' }
  ]

  const handleDatacreation = reason => {
    if (reason.name.length === 0) {
      notification.error({ message: t('A reason must be entered.'), placement: 'bottomRight' })

      return
    }

    createRejectionReason(user.institutions[0], reason, user)
      .then(json => {
        const newRejectionReasons = [...rejectionReasons]

        newRejectionReasons.push(json.data)

        setRejectionReasons(newRejectionReasons)
      })
      .catch((error) => { generalErrorHandler(error) })
  }

  const handleDataEdition = (reason) => {
    if (reason.name.length === 0) {
      notification.error({ message: t('A reason must be entered.'), placement: 'bottomRight' })

      return
    }

    updateRejectionReason(reason, user)
      .then(json => {
        const updatedReason = json.data

        setRejectionReasons(rejectionReasons.map(r => {
          if (r.id === updatedReason.id) {
            return updatedReason
          }

          return r
        }))
      })
      .catch((error) => { generalErrorHandler(error) })
  }

  const handleDataDeletion = reason => {
    deleteRejectionReason(reason, user)
      .then(json => {
        setRejectionReasons(rejectionReasons.filter(r => r.id !== reason.id))
      })
      .catch((error) => { generalErrorHandler(error) })
  }

  const handleInternshipRejection = async () => {
    if (!selectedReason && comment.length === 0) {
      notification.error({ message: t('Please complete your rejection reason.'), placement: 'bottomRight' })

      return
    }

    setLoading(true)

    refuseMaccsInternship(internship, user).then(() => {
      setLoading(false)
      handleClose(true)
    })
  }

  const handleClose = value => {
    setComment('')
    onClose(value)
  }

  return (
    <div>
      <Modal
        title={t('Refuse an internship')}
        visible={!!internship}
        onCancel={handleClose}
        footer={[
          <Button onClick={handleClose} key='cancel' type='default' loading={loading}>
            <FontAwesomeIcon icon='times' />&nbsp;{t('Cancel')}
          </Button>,
          <Button onClick={handleInternshipRejection} key='save' type='primary' loading={loading}>
            <FontAwesomeIcon icon='save' />&nbsp;{t('Save')}
          </Button>
        ]}
      >
        <Tabs defaultActiveKey='1' onChange={null} animated={false}>
          <TabPane tab={t('Refuse an internship')} key='1'>
            {t('Please select a reason to refuse this internship')} :
            <ReasonsRadioGroup
              comment={comment}
              reasons={rejectionReasons}
              selectedReason={selectedReason}
              onChange={setSelectedReason}
              onCommentChange={setComment}
            />
            <div className='v-spacing' />
            {rejectionReasons && (
              <div style={{ marginTop: '30px', color: '#9E9E9E' }}>
                {t('You can add rejection reasons in the second tab.')}
              </div>
            )}
          </TabPane>
          <TabPane tab={t('Rejection reasons')} key='2'>
            <SmartTable
              columns={columns}
              data={rejectionReasons}
              loading={loading}
              noDataText={t('You do not have any internship rejection reason.')}
              onDataAdd={handleDatacreation}
              onDataDelete={handleDataDeletion}
              onDataEdit={handleDataEdition}
            />
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  )
}

export default InternshipRejectionModal
